import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Loader2, Pencil, Trash2, Save, X, Upload, Package, MapPin } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FALLBACK_IMG = 'https://images.unsplash.com/photo-1595950653106-6c9ebd614d3a?w=300';

/** Admin-only panel to list, edit and delete test products. */
export default function TestProductsManager({ onChange }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // product being edited
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [query, setQuery] = useState('');

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/test-products`, { withCredentials: true });
      setProducts(res.data);
    } catch (e) {
      console.error(e);
      toast.error('Errore nel caricamento dei prodotti di test');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const openEdit = (product) => {
    setEditing({
      ...product,
      _imagesText: (product.immagini || []).join('\n'),
      _tagsText: (product.tag || []).join(', '),
    });
  };

  const closeEdit = () => setEditing(null);

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/upload`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const url = `${process.env.REACT_APP_BACKEND_URL}${res.data.url}`;
      setEditing((prev) => {
        const existing = (prev._imagesText || '').split('\n').map((l) => l.trim()).filter(Boolean);
        return { ...prev, _imagesText: [url, ...existing].join('\n') };
      });
      toast.success('Immagine caricata');
    } catch (err) {
      console.error(err);
      toast.error('Upload immagine fallito');
    } finally {
      setUploading(false);
      e.target.value = ''; // reset input
    }
  };

  const handleSave = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      const payload = {
        nome: editing.nome?.trim() || '',
        descrizione: editing.descrizione || '',
        prezzo: editing.prezzo === '' || editing.prezzo == null ? null : parseFloat(editing.prezzo),
        sconto: editing.sconto === '' || editing.sconto == null ? null : parseInt(editing.sconto, 10),
        disponibilita: editing.disponibilita || 'disponibile',
        immagini: (editing._imagesText || '')
          .split('\n')
          .map((l) => l.trim())
          .filter(Boolean),
        tag: (editing._tagsText || '')
          .split(',')
          .map((t) => t.trim())
          .filter(Boolean),
      };
      const res = await axios.patch(
        `${API}/admin/test-products/${editing.id}`,
        payload,
        { withCredentials: true },
      );
      setProducts((prev) => prev.map((p) => (p.id === editing.id ? { ...p, ...res.data, negozio_nome: p.negozio_nome, negozio_citta: p.negozio_citta } : p)));
      toast.success('Prodotto aggiornato');
      closeEdit();
      onChange?.();
    } catch (err) {
      const detail = err.response?.data?.detail || 'Errore durante il salvataggio';
      toast.error(typeof detail === 'string' ? detail : 'Errore durante il salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (product) => {
    if (!window.confirm(`Eliminare definitivamente "${product.nome}"?`)) return;
    try {
      await axios.delete(`${API}/admin/test-products/${product.id}`, { withCredentials: true });
      setProducts((prev) => prev.filter((p) => p.id !== product.id));
      toast.success('Prodotto eliminato');
      onChange?.();
    } catch (err) {
      console.error(err);
      toast.error('Errore eliminazione');
    }
  };

  const filtered = query
    ? products.filter((p) => {
        const q = query.toLowerCase();
        return (
          (p.nome || '').toLowerCase().includes(q) ||
          (p.negozio_nome || '').toLowerCase().includes(q) ||
          (p.categoria || '').toLowerCase().includes(q)
        );
      })
    : products;

  return (
    <div className="bg-white rounded-2xl shadow-sm p-4 space-y-3" data-testid="test-products-manager">
      <div className="flex flex-col sm:flex-row gap-2 sm:items-center sm:justify-between">
        <div>
          <h3 className="font-outfit font-semibold text-slate-800">
            Prodotti di test ({products.length})
          </h3>
          <p className="text-xs text-slate-500">Modifica o elimina i singoli prodotti generati dal seeder.</p>
        </div>
        <div className="flex gap-2">
          <Input
            placeholder="Cerca per nome, negozio o categoria…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="w-full sm:w-72 rounded-xl"
            data-testid="test-products-search"
          />
          <Button
            onClick={fetchProducts}
            variant="outline"
            size="sm"
            disabled={loading}
            data-testid="test-products-refresh"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Aggiorna'}
          </Button>
        </div>
      </div>

      {loading && products.length === 0 ? (
        <div className="py-10 text-center">
          <Loader2 className="w-6 h-6 animate-spin text-blue-600 mx-auto" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="py-10 text-center text-slate-500">
          <Package className="w-10 h-10 mx-auto mb-2 text-slate-300" />
          {products.length === 0 ? 'Nessun prodotto di test attivo.' : 'Nessun risultato per la ricerca.'}
        </div>
      ) : (
        <div className="divide-y border border-slate-100 rounded-xl overflow-hidden">
          {filtered.map((p) => (
            <div
              key={p.id}
              className="p-3 flex items-center gap-3 hover:bg-slate-50 transition-colors"
              data-testid={`test-product-row-${p.id}`}
            >
              <img
                src={p.immagini?.[0] || FALLBACK_IMG}
                alt=""
                className="w-14 h-14 rounded-lg object-cover flex-shrink-0 bg-slate-100"
                onError={(e) => { e.target.src = FALLBACK_IMG; }}
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-medium text-slate-800 truncate">{p.nome}</p>
                  {p.disponibilita !== 'disponibile' && (
                    <Badge className="bg-red-100 text-red-700 text-xs">Esaurito</Badge>
                  )}
                  {p.sconto ? (
                    <Badge className="bg-orange-100 text-orange-700 text-xs">-{p.sconto}%</Badge>
                  ) : null}
                </div>
                <p className="text-xs text-slate-500 truncate flex items-center gap-1">
                  <MapPin className="w-3 h-3" />
                  {p.negozio_nome} {p.negozio_citta ? `· ${p.negozio_citta}` : ''}
                </p>
                <p className="text-xs text-slate-600 mt-0.5">
                  {p.prezzo != null ? `${Number(p.prezzo).toFixed(2)} €` : 'Prezzo in negozio'}
                </p>
              </div>
              <div className="flex gap-1 flex-shrink-0">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => openEdit(p)}
                  data-testid={`test-product-edit-${p.id}`}
                >
                  <Pencil className="w-3.5 h-3.5" />
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-red-600 hover:bg-red-50"
                  onClick={() => handleDelete(p)}
                  data-testid={`test-product-delete-${p.id}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Edit Modal */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && closeEdit()}>
        <DialogContent className="sm:max-w-lg bg-white max-h-[90vh] overflow-y-auto" data-testid="test-product-edit-modal">
          <DialogHeader>
            <DialogTitle className="font-outfit text-lg">Modifica prodotto di test</DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              {editing?.negozio_nome} {editing?.negozio_citta ? `· ${editing.negozio_citta}` : ''}
            </DialogDescription>
          </DialogHeader>

          {editing && (
            <div className="space-y-3 pt-2">
              <div>
                <Label className="text-xs font-semibold text-slate-700">Nome</Label>
                <Input
                  value={editing.nome || ''}
                  onChange={(e) => setEditing({ ...editing, nome: e.target.value })}
                  className="rounded-xl mt-1"
                  data-testid="edit-product-name"
                />
              </div>

              <div>
                <Label className="text-xs font-semibold text-slate-700">Descrizione</Label>
                <Textarea
                  value={editing.descrizione || ''}
                  onChange={(e) => setEditing({ ...editing, descrizione: e.target.value })}
                  rows={3}
                  className="rounded-xl mt-1 resize-none"
                  data-testid="edit-product-description"
                />
              </div>

              <div className="grid grid-cols-3 gap-2">
                <div>
                  <Label className="text-xs font-semibold text-slate-700">Prezzo (€)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={editing.prezzo ?? ''}
                    onChange={(e) => setEditing({ ...editing, prezzo: e.target.value })}
                    className="rounded-xl mt-1"
                    data-testid="edit-product-price"
                  />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-slate-700">Sconto (%)</Label>
                  <Input
                    type="number"
                    min="0"
                    max="90"
                    value={editing.sconto ?? ''}
                    onChange={(e) => setEditing({ ...editing, sconto: e.target.value })}
                    className="rounded-xl mt-1"
                    data-testid="edit-product-discount"
                  />
                </div>
                <div>
                  <Label className="text-xs font-semibold text-slate-700">Disponibilità</Label>
                  <Select
                    value={editing.disponibilita || 'disponibile'}
                    onValueChange={(v) => setEditing({ ...editing, disponibilita: v })}
                  >
                    <SelectTrigger className="rounded-xl mt-1" data-testid="edit-product-availability">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="disponibile">Disponibile</SelectItem>
                      <SelectItem value="esaurito">Esaurito</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label className="text-xs font-semibold text-slate-700">Tag (separati da virgola)</Label>
                <Input
                  value={editing._tagsText || ''}
                  onChange={(e) => setEditing({ ...editing, _tagsText: e.target.value })}
                  placeholder="es. bricolage, casa, utensili"
                  className="rounded-xl mt-1"
                  data-testid="edit-product-tags"
                />
              </div>

              <div>
                <div className="flex items-center justify-between">
                  <Label className="text-xs font-semibold text-slate-700">
                    Immagini (una URL per riga)
                  </Label>
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleImageUpload}
                      data-testid="edit-product-upload"
                    />
                    <span className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 hover:underline">
                      {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
                      {uploading ? 'Caricamento…' : 'Carica nuova foto'}
                    </span>
                  </label>
                </div>
                <Textarea
                  value={editing._imagesText || ''}
                  onChange={(e) => setEditing({ ...editing, _imagesText: e.target.value })}
                  rows={3}
                  placeholder="https://example.com/img1.jpg"
                  className="rounded-xl mt-1 resize-none text-xs font-mono"
                  data-testid="edit-product-images"
                />
                {/* Image preview */}
                {(editing._imagesText || '').trim() && (
                  <div className="flex gap-2 mt-2 overflow-x-auto pb-1">
                    {(editing._imagesText || '')
                      .split('\n')
                      .map((l) => l.trim())
                      .filter(Boolean)
                      .slice(0, 6)
                      .map((url, i) => (
                        <img
                          key={`${url}-${i}`}
                          src={url}
                          alt=""
                          className="w-16 h-16 rounded-lg object-cover bg-slate-100 flex-shrink-0"
                          onError={(e) => { e.target.style.opacity = '0.3'; }}
                        />
                      ))}
                  </div>
                )}
              </div>

              <div className="flex gap-2 pt-2">
                <Button
                  variant="outline"
                  onClick={closeEdit}
                  className="flex-1 rounded-xl"
                  disabled={saving}
                  data-testid="edit-product-cancel"
                >
                  <X className="w-4 h-4 mr-1" /> Annulla
                </Button>
                <Button
                  onClick={handleSave}
                  disabled={saving}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white rounded-xl"
                  data-testid="edit-product-save"
                >
                  {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                  Salva
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
